import React, { useMemo, useState } from 'react'
import classNames from 'classnames'

import { Centre } from '../../../../types/centre'
import { Item, WithNavigationMenu, ParentItem } from '../types'

import style from './index.module.scss'
import { CentreSelector } from './centre-selector'
import Nav from '.'

const lookupPath = (items: Item[], path: string[]): number[] => {
  if (!items) {
    return []
  }
  const [key, ...rest] = path
  const i = items.findIndex(item => (item as ParentItem)?.key === key)
  if (i === -1) {
    return []
  }
  const item = items[i] as ParentItem
  return [i, ...lookupPath(item.items, rest)]
}

const Main: React.FC<{
  country: string
  centres?: Centre[]
  centre?: Centre & WithNavigationMenu
  shop?: WithNavigationMenu
  giftCards?: WithNavigationMenu
  initialPath?: string[]
  onClose: () => void
}> = ({ country, centres, centre, shop, giftCards, initialPath, onClose }) => {
  const findACentreNodes = useMemo(() => {
    if (!centres) {
      return undefined
    }

    const centresByRegion = centres.reduce<{ [key: string]: Array<Centre> }>(
      (acc, c) => {
        let arr = acc[c.state]
        if (!arr) {
          arr = []
          acc[c.state] = arr
        }
        arr.push(c)
        return acc
      },
      {}
    )

    return [
      () => <CentreSelector centres={centres} centre={centre} />,
      () => (
        <div className={classNames(style.subtitle, style.regions)}>Regions</div>
      ),
      ...(country === 'New Zealand'
        ? [
            ['AUCKLAND', 'Auckland'],
            ['CHRISTCHURCH', 'Christchurch'],
          ]
        : [
            ['ACT', 'Australian Capital Territory'],
            ['NSW', 'New South Wales'],
            ['QLD', 'Queensland'],
            ['SA', 'South Australia'],
            ['VIC', 'Victoria'],
            ['WA', 'Western Australia'],
          ]
      )
        .filter(([key]) => centresByRegion[key])
        .map(([key, title]) => ({
          title,
          items: centresByRegion[key].map(c => ({
            label: c.title,
            url: `/${c.slug}`,
          })),
        })),
    ]
  }, [country, centres, centre])

  const items = useMemo(
    () => [
      shop?.navigationMenu?.length
        ? {
            key: 'shop',
            title: 'Shop online',
            items: shop.navigationMenu,
            className: classNames(style['item-big']),
          }
        : undefined,
      centre
        ? {
            key: 'centre',
            title: centre.title,
            hideTitle: true,
            items: [
              () => (
                <div className={classNames(style.item)}>
                  <div className={classNames(style.title)}>{centre.title}</div>
                  <button
                    className={classNames(style.link)}
                    onClick={() =>
                      setPath(lookupPath(items, ['find-a-centre']))
                    }
                  >
                    Change
                  </button>
                </div>
              ),
              {
                label: 'Home',
                url: `/${centre.slug}`,
              },
              ...centre.navigationMenu,
            ],
            className: classNames(style['item-big']),
          }
        : findACentreNodes && {
            key: 'find-a-centre',
            title: 'Find a centre',
            items: findACentreNodes,
            className: classNames(style['item-big']),
          },
      giftCards && {
        key: 'gift-cards',
        title: 'Gift Cards',
        items: giftCards.navigationMenu,
        className: classNames(style['item-big']),
      },
      {
        label: 'The Edit',
        url: '/the-edit',
        className: classNames(style['item-big']),
      },
      {
        label: 'Westfield Plus',
        url: '/westfield-plus',
        attrs: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
        className: classNames(style['item-big']),
      },
      centre &&
        findACentreNodes && {
          key: 'find-a-centre',
          title: 'Find a centre',
          items: findACentreNodes,
        },
      {
        label: 'Customer Support',
        url:
          country === 'New Zealand'
            ? 'https://support.westfield.co.nz'
            : 'https://support.westfield.com.au',
      },
    ],
    [country, centre, shop, giftCards, findACentreNodes]
  )

  const [path, setPath] = useState(
    () => initialPath && lookupPath(items, initialPath)
  )

  return <Nav items={items} initialPath={path} onClose={onClose} />
}

export default Main
